import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Sparkles, Wand2, PartyPopper, X } from "lucide-react";
import { orgApi } from "../lib/organizerApi";
import EventBannerStudio from "./EventBannerStudio";

const EMPTY = {
  name: "",
  slug: "",
  event_date: "",
  event_time: "",
  end_time: "",
  location: "",
  venue_name: "",
  address: "",
  maps_url: "",
  subtitle: "",
  description: "",
  hero_image_url: "",
  banner_image_url: "",
  primary_color: "",
  secondary_color: "",
  ticket_footer_text: "",
  checkin_pin: "",
};

export default function OrganizerEventEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [showCelebration, setShowCelebration] = useState(false);
  const [showBannerStudio, setShowBannerStudio] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await orgApi.get(`/organizer/events/${slug}`);
        setForm((s) => ({ ...s, ...Object.fromEntries(Object.entries(data).filter(([k]) => k in EMPTY)) }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Không thể tải sự kiện.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // Strip empty strings (let backend keep DB defaults / null)
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );
      if (isNew) {
        const { data } = await orgApi.post("/organizer/events", payload);
        flash("Đã tạo sự kiện");
        navigate(`/organizer/events/${data.slug}/edit`, { replace: true, state: { justCreated: true } });
        // Show the celebration on the freshly-redirected page
        setTimeout(() => setShowCelebration(true), 200);
      } else {
        await orgApi.put(`/organizer/events/${slug}`, payload);
        flash("Đã lưu thay đổi");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Thao tác thất bại.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linen">
        <Loader2 className="w-6 h-6 animate-spin text-espresso/60" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen" data-testid="organizer-event-editor">
      <header className="border-b border-espresso/10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/organizer/dashboard" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm" data-testid="org-back">
            <ArrowLeft className="w-4 h-4" /> Quay lại Dashboard
          </Link>
          <div className="inline-flex items-center gap-2 text-espresso">
            <Sparkles className="w-4 h-4" />
            <span className="tracking-[0.28em] uppercase text-sm">Nail Viet Club</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="text-xs uppercase tracking-[0.28em] text-espresso-muted">{isNew ? "Tạo sự kiện" : "Chỉnh sửa"}</p>
        <h1 className="text-3xl sm:text-4xl font-serif text-espresso mt-1">
          {isNew ? "Sự kiện mới" : form.name || "Chỉnh sửa sự kiện"}
        </h1>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-espresso/10 space-y-6">
          <Section title="Thông tin cơ bản">
            <Field label="Tên sự kiện *" testId="ev-name" value={form.name} onChange={set("name")} required />
            {!isNew && <Field label="Slug (không thể đổi)" testId="ev-slug" value={form.slug} disabled />}
            <Row>
              <Field label="Ngày *" type="date" testId="ev-date" value={form.event_date} onChange={set("event_date")} required />
              <Field label="Giờ bắt đầu" testId="ev-time" value={form.event_time} onChange={set("event_time")} placeholder="09:00 AM" />
              <Field label="Giờ kết thúc" testId="ev-end-time" value={form.end_time} onChange={set("end_time")} placeholder="06:00 PM" />
            </Row>
            <Field label="Địa điểm *" testId="ev-location" value={form.location} onChange={set("location")} required placeholder="Houston, TX" />
            <Row>
              <Field label="Tên địa điểm" testId="ev-venue" value={form.venue_name} onChange={set("venue_name")} placeholder="George R. Brown Convention Center" />
              <Field label="Địa chỉ chi tiết" testId="ev-address" value={form.address} onChange={set("address")} />
            </Row>
            <Field label="Google Maps URL" testId="ev-maps" value={form.maps_url} onChange={set("maps_url")} placeholder="https://maps.google.com/?q=..." />
            <Field label="Phụ đề (subtitle)" testId="ev-subtitle" value={form.subtitle} onChange={set("subtitle")} />
            <Field label="Mô tả" testId="ev-description" value={form.description} onChange={set("description")} multiline />
          </Section>

          <Section title="Hình ảnh & Thương hiệu">
            <Field label="Ảnh hero URL" testId="ev-hero" value={form.hero_image_url} onChange={set("hero_image_url")} />
            <Field label="Ảnh banner URL" testId="ev-banner" value={form.banner_image_url} onChange={set("banner_image_url")} />
            <Row>
              <Field label="Màu chính (hex)" testId="ev-primary-color" value={form.primary_color} onChange={set("primary_color")} placeholder="#3F2A1B" />
              <Field label="Màu phụ (hex)" testId="ev-secondary-color" value={form.secondary_color} onChange={set("secondary_color")} placeholder="#E2C57B" />
            </Row>
          </Section>

          <Section title="Cài đặt Kiosk & Vé">
            <Row>
              <Field label="Mã PIN check-in (4–8 ký tự)" testId="ev-pin" value={form.checkin_pin} onChange={set("checkin_pin")} placeholder="2468" />
              <Field label="Chân vé (Footer)" testId="ev-footer" value={form.ticket_footer_text} onChange={set("ticket_footer_text")} placeholder="Cảm ơn quý khách đã đến!" />
            </Row>
          </Section>

          {error && (
            <div data-testid="org-editor-error" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-2">
            <Link to="/organizer/dashboard" className="text-sm text-espresso-soft hover:text-espresso">Hủy</Link>
            <button
              type="submit"
              disabled={busy}
              data-testid="org-editor-submit"
              className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {busy ? "Đang lưu…" : isNew ? "Tạo sự kiện" : "Lưu thay đổi"}
            </button>
          </div>
        </form>

        {!isNew && (
          <div className="mt-8" data-testid="event-editor-banner-studio-section">
            {!showBannerStudio ? (
              <div className="rounded-3xl bg-gradient-to-br from-espresso to-[#5C4030] text-cream p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 20%, rgba(226,197,123,0.6), transparent 60%)" }} />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold-300 flex items-center gap-1.5">
                      <Wand2 className="w-3 h-3" /> AI Creative Studio
                    </div>
                    <h3 className="mt-1 font-serif text-2xl sm:text-3xl">Tạo banner AI cho sự kiện</h3>
                    <p className="mt-1 text-sm text-cream/80 max-w-lg">
                      AI sẽ thiết kế 3 phong cách độc đáo (cao cấp · tối giản · sôi động) cùng 7 kích thước
                      sẵn sàng cho web và mạng xã hội — không cần Canva.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBannerStudio(true)}
                    data-testid="open-banner-studio"
                    className="inline-flex items-center gap-2 bg-gold-500 text-charcoal rounded-full px-5 py-3 text-sm font-semibold hover:bg-gold-400 transition-all flex-shrink-0"
                  >
                    <Wand2 className="w-4 h-4" /> Mở Studio
                  </button>
                </div>
              </div>
            ) : (
              <EventBannerStudio
                eventSlug={slug}
                eventName={form.name}
                onSelected={(c) => {
                  if (c?.assets?.website_hero) {
                    setForm((s) => ({ ...s, hero_image_url: c.assets.website_hero }));
                  }
                }}
              />
            )}
          </div>
        )}
      </main>

      {showCelebration && !isNew && (
        <EventCreatedCelebration
          eventName={form.name}
          onClose={() => setShowCelebration(false)}
          onOpenStudio={() => { setShowCelebration(false); setShowBannerStudio(true); }}
        />
      )}

      {toast && (
        <div data-testid="org-editor-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-espresso text-cream rounded-full px-4 py-2 text-sm shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xs uppercase tracking-[0.28em] font-semibold text-espresso-muted">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function Field({ label, testId, type = "text", multiline = false, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label}</span>
      {multiline ? (
        <textarea
          data-testid={testId}
          rows={4}
          {...rest}
          className="mt-1 w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
        />
      ) : (
        <input
          type={type}
          data-testid={testId}
          {...rest}
          className="mt-1 w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso disabled:opacity-60"
        />
      )}
    </label>
  );
}

function EventCreatedCelebration({ eventName, onClose, onOpenStudio }) {
  return (
    <div
      data-testid="event-created-celebration"
      className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl p-7 sm:p-8 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="absolute top-4 right-4 text-espresso-muted hover:text-espresso"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="mx-auto w-14 h-14 rounded-2xl bg-gold-500 text-charcoal flex items-center justify-center">
          <PartyPopper className="w-6 h-6" />
        </div>
        <div className="mt-4 text-center">
          <h3 className="font-serif text-2xl sm:text-3xl text-espresso">
            Sự kiện của bạn đã được tạo!
          </h3>
          {eventName && (
            <p className="mt-1 text-sm text-espresso-soft truncate">{eventName}</p>
          )}
          <p className="mt-3 text-sm text-espresso-soft leading-relaxed">
            Bạn có muốn AI tạo banner chuyên nghiệp cho sự kiện này không?
            3 phong cách · 7 kích thước cho web & mạng xã hội — chỉ trong vài giây.
          </p>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpenStudio}
            data-testid="celebration-open-studio"
            className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-3 text-sm font-semibold"
          >
            <Wand2 className="w-4 h-4" /> Tạo banner AI
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="celebration-skip"
            className="inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-5 py-3 text-sm font-semibold"
          >
            Để sau
          </button>
        </div>
      </div>
    </div>
  );
}
